/**
 * Extra Material Hooks
 *
 * React Query hooks for extra materials / homework links.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import * as extraMaterialService from "../services/extraMaterialService";
import type {
  CreateExtraMaterialRequest,
  UpdateExtraMaterialRequest,
} from "../types/material";

export function useExtraMaterials(params?: {
  level?: string;
  classGroup?: string;
  scope?: string;
}) {
  return useQuery({
    queryKey: ["extra-materials", params],
    queryFn: () => extraMaterialService.getExtraMaterials(params),
    staleTime: 1000 * 60 * 2,
  });
}

export function useCreateExtraMaterial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateExtraMaterialRequest) =>
      extraMaterialService.createExtraMaterial(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["extra-materials"] });
      toast.success("Material extra publicado!");
    },
    onError: () => {
      toast.error("Erro ao publicar material extra");
    },
  });
}

export function useUpdateExtraMaterial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateExtraMaterialRequest }) =>
      extraMaterialService.updateExtraMaterial(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["extra-materials"] });
      toast.success("Material extra atualizado!");
    },
    onError: () => {
      toast.error("Erro ao atualizar material extra");
    },
  });
}

export function useDeleteExtraMaterial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => extraMaterialService.deleteExtraMaterial(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["extra-materials"] });
      toast.success("Material extra removido!");
    },
    onError: () => {
      toast.error("Erro ao remover material extra");
    },
  });
}
